import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import { ColorScheme } from 'mini-color-utils/lib/ColorScheme';
import { generateColorScheme, getDefaultColorSchemes } from 'mini-color-utils/lib/colorUtils';

const assignedColorSchemes: { [key: string]: ColorScheme } = {};

// Order of color schemes as needed in calendar (This is same order as Big OWA)
const colorSchemeIndexOrder: number[] = [0, 5, 1, 3, 8, 6, 7, 2, 4];

// All available color schemes to be used in calendar. This has to be initialized using the colorSchemeIndexOrder.
let availableColorSchemes: ColorScheme[];

/**
 * Generates color scheme array in color order
 * @returns all color schemes in color order
 */
export function generateAvailableColorSchemesInOrder(): ColorScheme[] {
    return colorSchemeIndexOrder.map<ColorScheme>(
        colorIndex => getDefaultColorSchemes()[colorIndex]
    );
}

/**
 * The available colors to be used in calendar as per the order.
 */
function getAvailableCalendarColorSchemes(): ColorScheme[] {
    if (!availableColorSchemes) {
        availableColorSchemes = generateAvailableColorSchemesInOrder().reverse();
    }
    return availableColorSchemes;
}

/**
 * Returns the calendar color scheme for a given calendar entry.
 * @param calendarEntry
 */
export default function getCalendarColorScheme(calendarEntry: CalendarEntry): ColorScheme {
    const calendarColorScheme = assignedColorSchemes[calendarEntry.ItemId.Id];
    if (!calendarColorScheme) {
        // time to generate a new color scheme for this calendar id and set it in assignedColorSchemes
        return assignCalendarColorScheme(calendarEntry);
    }
    return calendarColorScheme;
}

function getColorSchemeForCalendarColor(calendarColor: CalendarColor): ColorScheme {
    let colorSchemeForCalendarColor = null;
    const availableCalendarColorSchemes = getAvailableCalendarColorSchemes();
    if (calendarColor == undefined || calendarColor === CalendarColor.Auto) {
        // If Calendar color is undefined or auto, just pop it from the available calendar color schemes.
        colorSchemeForCalendarColor = availableCalendarColorSchemes.pop();
    } else {
        colorSchemeForCalendarColor = getDefaultColorSchemes()[calendarColor];

        // Remove the colorScheme from the available color schemes
        let foundColorSchemeIndex = -1;
        for (let index = 0; index < availableCalendarColorSchemes.length; index++) {
            if (availableColorSchemes[index] === colorSchemeForCalendarColor) {
                foundColorSchemeIndex = index;
                break;
            }
        }

        if (foundColorSchemeIndex !== -1) {
            availableColorSchemes.splice(foundColorSchemeIndex, 1);
        }
    }
    if (!availableColorSchemes.length) {
        availableColorSchemes = generateAvailableColorSchemesInOrder().reverse();
    }
    return colorSchemeForCalendarColor;
}

/**
 * Returns the color scheme for a given calendar entry and puts the old color back in available stack.
 * @param calendarEntry
 */
export function assignCalendarColorScheme(calendarEntry: CalendarEntry): ColorScheme {
    const calendarId = calendarEntry.ItemId.Id;
    let colorSchemeForCalendar;
    if (assignedColorSchemes[calendarId]) {
        // put the old color back into the available stack to be reused later
        getAvailableCalendarColorSchemes().push(assignedColorSchemes[calendarId]);

        // re-sort the available colors array
        const allColorsInOrder = generateAvailableColorSchemesInOrder().reverse();
        availableColorSchemes = allColorsInOrder.filter(
            value => availableColorSchemes.indexOf(value) > -1
        );
    }

    if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString !== '' &&
        getDefaultColorSchemes().filter(
            color =>
                color.schemePrimary.toLowerCase() === calendarEntry.HexColorString.toLowerCase()
        ).length > 0
    ) {
        colorSchemeForCalendar = generateColorScheme(calendarEntry.HexColorString);
    } else {
        colorSchemeForCalendar = getColorSchemeForCalendarColor(calendarEntry.CalendarColor);
    }

    assignedColorSchemes[calendarId] = colorSchemeForCalendar;

    return colorSchemeForCalendar;
}
